import type { AssessmentResponseSummary, ModuleResponseSummary } from '@/lib/assessments';

export type ModuleDiffStatus = 'unchanged' | 'improved' | 'regressed' | 'added' | 'removed';

export interface ModuleDiff {
  id: number;
  code: string;
  name: string;
  fromPct: number | null;
  toPct: number | null;
  delta: number; // pts; 0 when either side is missing
  fromTotal: number;
  toTotal: number;
  fromCompliant: number;
  toCompliant: number;
  status: ModuleDiffStatus;
}

export interface SnapshotDiff {
  totals: {
    fromPct: number;
    toPct: number;
    pctDelta: number;
    fromTotal: number;
    toTotal: number;
    fromCompliant: number;
    toCompliant: number;
    fromWarnings: number;
    toWarnings: number;
    warningsDelta: number;
  };
  modules: ModuleDiff[];
}

function modulePct(m: ModuleResponseSummary): number {
  const total = m.summary.itemResponses.total;
  if (total === 0) return 0;
  return Math.round((m.summary.itemResponses.meetsStandardCount / total) * 100);
}

function totalsFrom(modules: ModuleResponseSummary[]) {
  let total = 0;
  let compliant = 0;
  let warnings = 0;
  for (const m of modules) {
    total += m.summary.itemResponses.total;
    compliant += m.summary.itemResponses.meetsStandardCount;
    for (const w of m.summary.itemResponses.warnings) warnings += w.count;
  }
  return { total, compliant, warnings };
}

export function compareSnapshots(
  from: AssessmentResponseSummary,
  to: AssessmentResponseSummary,
): SnapshotDiff {
  const fromMap = new Map(from.modules.map((m) => [m.id, m]));
  const toMap = new Map(to.modules.map((m) => [m.id, m]));

  const fromTotals = totalsFrom(from.modules);
  const toTotals = totalsFrom(to.modules);
  const fromPct =
    fromTotals.total > 0
      ? Math.round((fromTotals.compliant / fromTotals.total) * 100)
      : 0;
  const toPct =
    toTotals.total > 0
      ? Math.round((toTotals.compliant / toTotals.total) * 100)
      : 0;

  const ids = new Set<number>([...fromMap.keys(), ...toMap.keys()]);
  const modules: ModuleDiff[] = [];
  for (const id of ids) {
    const a = fromMap.get(id);
    const b = toMap.get(id);
    const fromPctVal = a ? modulePct(a) : null;
    const toPctVal = b ? modulePct(b) : null;
    const delta =
      fromPctVal !== null && toPctVal !== null ? toPctVal - fromPctVal : 0;
    let status: ModuleDiffStatus;
    if (!a) status = 'added';
    else if (!b) status = 'removed';
    else if (delta > 0) status = 'improved';
    else if (delta < 0) status = 'regressed';
    else status = 'unchanged';

    modules.push({
      id,
      code: (b ?? a)!.code,
      name: (b ?? a)!.name,
      fromPct: fromPctVal,
      toPct: toPctVal,
      delta,
      fromTotal: a?.summary.itemResponses.total ?? 0,
      toTotal: b?.summary.itemResponses.total ?? 0,
      fromCompliant: a?.summary.itemResponses.meetsStandardCount ?? 0,
      toCompliant: b?.summary.itemResponses.meetsStandardCount ?? 0,
      status,
    });
  }

  // Largest delta first (regressions and improvements both bubble up). Within
  // same delta magnitude, prefer regressions to make problems prominent.
  modules.sort((x, y) => {
    const ax = Math.abs(x.delta);
    const ay = Math.abs(y.delta);
    if (ax !== ay) return ay - ax;
    if (x.delta !== y.delta) return x.delta - y.delta; // negatives first
    return x.code.localeCompare(y.code);
  });

  return {
    totals: {
      fromPct,
      toPct,
      pctDelta: toPct - fromPct,
      fromTotal: fromTotals.total,
      toTotal: toTotals.total,
      fromCompliant: fromTotals.compliant,
      toCompliant: toTotals.compliant,
      fromWarnings: fromTotals.warnings,
      toWarnings: toTotals.warnings,
      warningsDelta: toTotals.warnings - fromTotals.warnings,
    },
    modules,
  };
}
